import React, {forwardRef} from 'react'

import {useController, UseControllerProps, useFormContext} from 'react-hook-form'

import Input from '../Input'
import {IInputProps} from '../Input/Input'
import Textarea from '../Textarea'
import {ITextareaProps} from '../Textarea/Textarea'

interface IControlledInputProps
	extends Omit<ITextareaProps, 'name' | 'value'>,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		Omit<UseControllerProps<Record<string, any>>, 'onFocus' | 'defaultValue'> {}

const ControlledTextarea = forwardRef<HTMLTextAreaElement, IControlledInputProps>(
	(props: IControlledInputProps, forwardedRef) => {
		const {name, rules, ...inputProps} = props
		const formContext = useFormContext()
		const {
			control,
			formState: {errors},
		} = formContext
		const {field} = useController({name, control, rules})

		const onBlur: React.FocusEventHandler<HTMLTextAreaElement> = (e) => {
			if (typeof field.onBlur === 'function')
				(field as {onBlur: React.FocusEventHandler<HTMLTextAreaElement>}).onBlur(e)
			inputProps.onBlur && inputProps.onBlur(e)
		}

		return (
			<Textarea
				{...inputProps}
				{...field}
				onBlur={onBlur}
				isError={!!errors[name]?.message}
				ref={forwardedRef}
			/>
		)
	},
)

ControlledTextarea.displayName = 'ControlledTextarea'

interface IFormTextInputProps extends IControlledInputProps {}

const FieldTextarea = forwardRef<HTMLTextAreaElement, IFormTextInputProps>(
	(props, forwardedRef) => {
		const {name, ...inputProps} = props
		const formContext = useFormContext()

		if (!formContext || !name) {
			const errorMessage = !name
				? 'Form field must have a "name" prop!'
				: 'Form field must be a descendant of `FormProvider` as it uses `useFormContext`!'
			return <Input {...(inputProps as IInputProps)} error={errorMessage} />
		}

		return <ControlledTextarea {...props} ref={forwardedRef} />
	},
)

FieldTextarea.displayName = 'FieldTextarea'

export default FieldTextarea
